import {html} from "lit";
import {ifDefined} from "lit/directives/if-defined.js";
import AuiInput, {formatNativeInputValue} from "./aui-input.js";
import {getLineBreaks} from "../../Utils/DOMUtils.js";
import AuiInputStyle from "./aui-input-style.scss";
import AuiTextareaStyle from "./aui-textarea-style.scss";
import {classMap} from "lit/directives/class-map.js";

/**
 * Autogrow textarea by exactly replicate the look, content, and position of the textarea in another dummy element which
 * will be hidden and be used to calculate the height and rendered newlines.
 *
 * TODO: Window resize, update rows
 *
 * @link https://css-tricks.com/the-cleanest-trick-for-autogrowing-textareas
 */
export class AuiTextarea extends AuiInput {

	static get properties() {
		return {
			type: { type: String, reflect: true },
			placeholder: { type: String, reflect: true },

			minLines: { type: Number, attribute: "min-lines", reflect: true },
			maxLines: { type: Number, attribute: "max-lines", reflect: true },
		};
	}

	set maxLines(value) {
		const oldValue = this.__maxLines;
		this.__maxLines = value;
		this.requestUpdate("maxLines", oldValue);

		if(oldValue && !this.__maxLines)
			this.GrowWrapper.style.height = "";
	}

	get maxLines() {
		return this.__maxLines;
	}

	get Input() {
		if(!this._input)
			this._input = this.shadowRoot.querySelector("textarea");
		return this._input;
	}

	get Dummy() {
		if(!this._dummy)
			this._dummy = this.shadowRoot.querySelector("#Dummy");
		return this._dummy;
	}

	get GrowWrapper() {
		if(!this._growWrapper)
			this._growWrapper = this.shadowRoot.querySelector("#GrowWrapper");
		return this._growWrapper;
	}

	constructor() {
		super();
		this.minLines = 2;
		this.maxLines = undefined;
	}

	update(changedProperties) {
		super.update(changedProperties);

		if(changedProperties.size === 0 || changedProperties.has("value") || changedProperties.has("maxLines")) {
			this.updateSize();
		}
	}

	updateSize() {
		if(!this.maxLines || this.maxLines < this.minLines)
			return;

		const lines = getLineBreaks(this.Dummy, this.maxLines + 1);
		if(lines.length > this.maxLines) {
			let maxHeight = lines.slice(0, this.maxLines).reduce((total, line) => total + line.height, 0);
			this.GrowWrapper.style.height = maxHeight + "px";
		} else {
			this.GrowWrapper.style.height = "";
		}
	}

	static get styles() {
		return [AuiInputStyle, AuiTextareaStyle];
	}

	renderNativeInput() {
		return html`
			<div id="GrowWrapper" class="grow-wrap">
				${this.renderTextarea()}
				${this.renderDummy()}
			</div>
		`;
	}

	renderTextarea() {
		return html`<textarea
			class=${classMap(this.getInputClassMap())}
			rows=${this.minLines}
			placeholder=${ifDefined(this.placeholder)}
			maxlength=${ifDefined(this.maxlength)}
			readonly=${ifDefined(this.isReadonly)}
			.value=${formatNativeInputValue(this.value)}
			@input=${e => this.value = e.target.value}
		></textarea>`;
	}

	/**
	 * Renders a dummy textarea with the same content, which is then be used to height using css.
	 *
	 * @return {TemplateResult}
	 */
	renderDummy() {
		// Text ending with an blank line is not rendered, but in order to correctly detect line breaks, we need
		// to add an additional blank link which is then rendered.
		let value = this.value;
		if(value && value.substr(-1) === "\n")
			value += "\ufeff";

		return html`<div id="Dummy" class="dummy">${value}</div>`;
	}

}

export default AuiTextarea;

customElements.define("aui-textarea", AuiTextarea);